import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Plus, Edit2, Trash2, ChevronDown, ChevronUp, Settings, Check, X, ArrowUpDown, Filter, FileText, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { formatINR, stripCommas } from '../lib/numberFormat';

const MTFManagement = ({ user, setUser }) => {
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [accountSortConfig, setAccountSortConfig] = useState({});
  const [accountFilters, setAccountFilters] = useState({});
  const [showFilters, setShowFilters] = useState({});
  const [formData, setFormData] = useState({
    member_id: '',
    bank_name: '',
    account_number: '',
    total_loan: 0,
    interest_rate: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, membersRes, transactionsRes] = await Promise.all([
        api.get('/mtf-accounts'),
        api.get('/family-members'),
        api.get('/transactions')
      ]);
      setAccounts(accountsRes.data);
      setMembers(membersRes.data);
      // Filter to only MTF-related transactions
      const mtfTransactions = transactionsRes.data.filter(t => 
        t.purchase_type === 'MTF' || t.transaction_type === 'buy_mtf' || t.mtf_account_id
      );
      setTransactions(mtfTransactions);
    } catch (err) {
      toast.error('Failed to load MTF accounts');
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? member.name : 'Unknown';
  };

  const handleEdit = (account) => {
    setEditingId(account.id);
    setEditFormData({
      bank_name: account.bank_name,
      account_number: account.account_number,
      total_loan: account.total_loan,
      interest_rate: account.interest_rate,
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/mtf-accounts/${id}`, editFormData);
      toast.success('MTF account updated');
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update account');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/mtf-accounts', formData);
      toast.success('MTF account created');
      setOpen(false);
      setFormData({
        member_id: '',
        bank_name: '',
        account_number: '',
        total_loan: 0,
        interest_rate: 0,
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to create account');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this MTF account?')) return;
    try {
      await api.delete(`/mtf-accounts/${id}`);
      toast.success('Account deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete account');
    }
  };

  const updateLoan = async (accountId, newAmount) => {
    try {
      await api.patch(`/mtf-accounts/${accountId}?total_loan=${newAmount}`);
      toast.success('Loan amount updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update loan');
    }
  };

  const calculateMonthlyInterest = (loanAmount, interestRate) => {
    return (loanAmount * interestRate) / 100 / 12;
  };

  const toggleExpanded = (accountId) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const toggleFilters = (accountId) => {
    setShowFilters(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const getAccountTransactions = (accountId) => {
    // Get transactions for this MTF account
    return transactions.filter(t => t.mtf_account_id === accountId);
  };

  const getSortConfig = (accountId) => {
    return accountSortConfig[accountId] || { key: 'date', direction: 'desc' };
  };

  const getFilters = (accountId) => {
    return accountFilters[accountId] || { symbol: '', type: '', dateFrom: '', dateTo: '' };
  };

  const handleSort = (accountId, key) => {
    setAccountSortConfig(prev => ({
      ...prev,
      [accountId]: {
        key,
        direction: prev[accountId]?.key === key && prev[accountId]?.direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const handleFilterChange = (accountId, field, value) => {
    setAccountFilters(prev => ({
      ...prev,
      [accountId]: {
        ...getFilters(accountId),
        [field]: value
      }
    }));
  };

  const clearFilters = (accountId) => {
    setAccountFilters(prev => ({
      ...prev,
      [accountId]: { symbol: '', type: '', dateFrom: '', dateTo: '' }
    }));
  };

  const getFilteredAndSortedTransactions = (accountId) => {
    let accountTxns = getAccountTransactions(accountId);
    const filters = getFilters(accountId);
    const sortConfig = getSortConfig(accountId);

    // Apply filters
    if (filters.symbol) {
      accountTxns = accountTxns.filter(t => 
        t.symbol.toLowerCase().includes(filters.symbol.toLowerCase())
      );
    }
    if (filters.type) {
      accountTxns = accountTxns.filter(t => t.transaction_type === filters.type);
    }
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      accountTxns = accountTxns.filter(t => new Date(t.transaction_date) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59);
      accountTxns = accountTxns.filter(t => new Date(t.transaction_date) <= toDate);
    }

    // Apply sorting
    accountTxns.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortConfig.key) {
        case 'date':
          aVal = new Date(a.transaction_date).getTime();
          bVal = new Date(b.transaction_date).getTime();
          break;
        case 'symbol':
          aVal = a.symbol || '';
          bVal = b.symbol || '';
          return sortConfig.direction === 'asc' 
            ? aVal.localeCompare(bVal) 
            : bVal.localeCompare(aVal);
        case 'quantity':
          aVal = a.quantity || 0;
          bVal = b.quantity || 0;
          break;
        case 'price':
          aVal = a.price || 0;
          bVal = b.price || 0;
          break;
        case 'total':
          aVal = (a.quantity || 0) * (a.price || 0);
          bVal = (b.quantity || 0) * (b.price || 0);
          break;
        case 'type':
          aVal = a.transaction_type || '';
          bVal = b.transaction_type || '';
          return sortConfig.direction === 'asc' 
            ? aVal.localeCompare(bVal) 
            : bVal.localeCompare(aVal);
        default:
          aVal = new Date(a.transaction_date).getTime();
          bVal = new Date(b.transaction_date).getTime();
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return accountTxns;
  };

  const getUniqueSymbols = (accountId) => {
    const accountTxns = getAccountTransactions(accountId);
    return [...new Set(accountTxns.map(t => t.symbol).filter(Boolean))].sort();
  };

  const getTransactionTypeLabel = (type) => {
    switch(type) {
      case 'buy': return 'Buy';
      case 'buy_mtf': return 'Buy (MTF)';
      case 'sell': return 'Sell';
      default: return type;
    }
  };

  const renderSortHeader = (accountId, key, label) => {
    const sortConfig = getSortConfig(accountId);
    return (
      <th 
        className="text-left py-3 px-3 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(accountId, key)}
      >
        <div className="flex items-center gap-1">
          {label}
          {sortConfig.key === key ? (
            <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
          ) : (
            <ArrowUpDown size={12} className="text-gray-400" />
          )}
        </div>
      </th>
    );
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-20">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="mtf-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">MTF Accounts</h1>
            <p className="text-gray-600">Manage Margin Trading Facility accounts and view transactions</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setEditMode(!editMode)}
              className={`btn-outline flex items-center gap-2 ${editMode ? 'bg-amber-50 border-amber-500 text-amber-700' : ''}`}
            >
              <Settings size={18} />
              {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-mtf-button" className="btn-primary flex items-center gap-2">
                  <Plus size={20} />
                  Add MTF Account
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create MTF Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Member *</label>
                  <select
                    data-testid="mtf-member-select"
                    className="input-field w-full"
                    value={formData.member_id}
                    onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                    required
                  >
                    <option value="">Select Member</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.relation})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Bank Name *</label>
                  <input
                    type="text"
                    data-testid="mtf-bank-input"
                    className="input-field w-full"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Account Number *</label>
                  <input
                    type="text"
                    data-testid="mtf-account-input"
                    className="input-field w-full"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Total Loan Amount</label>
                  <input
                    type="text" inputMode="decimal"
                    data-testid="mtf-loan-input"
                   
                    className="input-field w-full"
                    value={formatINR(formData.total_loan)}
                    onChange={(e) => setFormData({ ...formData, total_loan: parseFloat(stripCommas(e.target.value)) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Interest Rate (% per annum) *</label>
                  <input
                    type="text" inputMode="decimal"
                    data-testid="mtf-interest-input"
                   
                    className="input-field w-full"
                    value={formatINR(formData.interest_rate)}
                    onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(stripCommas(e.target.value)) })}
                    required
                  />
                </div>
                <Button type="submit" data-testid="submit-mtf-button" className="btn-primary w-full">
                  Create Account
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {editMode && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            ⚠️ Edit Mode is ON - You can now edit or delete accounts. Click Exit Edit Mode when done.
          </div>
        )}

        {accounts.length === 0 ? (
          <div className="card text-center py-12" data-testid="empty-mtf">
            <p className="text-gray-500">No MTF accounts yet. Create your first account or upload MTF Excel file.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {accounts.map((account) => {
              const member = members.find((m) => m.id === account.member_id);
              const monthlyInterest = calculateMonthlyInterest(account.total_loan, account.interest_rate);
              const accountTxns = getAccountTransactions(account.id);
              const filteredTxns = getFilteredAndSortedTransactions(account.id);
              const isExpanded = expandedAccounts[account.id];
              const filtersVisible = showFilters[account.id];
              const filters = getFilters(account.id);
              const hasActiveFilters = filters.symbol || filters.type || filters.dateFrom || filters.dateTo;
              
              const totalValue = accountTxns.reduce((sum, t) => sum + (t.quantity || 0) * (t.price || 0), 0);

              return (
                <div key={account.id} className="card" data-testid={`mtf-account-${account.id}`}>
                  {editingId === account.id ? (
                    // Edit Form
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-lg mb-3">Edit MTF Account</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Bank Name</label>
                          <input
                            type="text"
                            className="input-field w-full"
                            value={editFormData.bank_name}
                            onChange={(e) => setEditFormData({ ...editFormData, bank_name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Account Number</label>
                          <input
                            type="text"
                            className="input-field w-full"
                            value={editFormData.account_number}
                            onChange={(e) => setEditFormData({ ...editFormData, account_number: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Total Loan</label>
                          <input
                            type="text" inputMode="decimal"
                            className="input-field w-full"
                            value={formatINR(editFormData.total_loan)}
                            onChange={(e) => setEditFormData({ ...editFormData, total_loan: parseFloat(stripCommas(e.target.value)) })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Interest Rate (%)</label>
                          <input
                            type="text" inputMode="decimal"
                           
                            className="input-field w-full"
                            value={formatINR(editFormData.interest_rate)}
                            onChange={(e) => setEditFormData({ ...editFormData, interest_rate: parseFloat(stripCommas(e.target.value)) })}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button onClick={() => handleSaveEdit(account.id)} className="btn-primary flex items-center gap-1">
                          <Check size={16} /> Save
                        </Button>
                        <Button onClick={handleCancelEdit} className="btn-outline flex items-center gap-1">
                          <X size={16} /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Account Header */}
                      <div className="flex items-start justify-between mb-6 pb-4 border-b-2 border-primary">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-3xl font-bold text-primary">{member?.name}</h2>
                            <span className="badge badge-success text-lg px-4 py-1">MTF</span>
                          </div>
                          <div className="flex items-center gap-4 text-gray-600">
                            <p className="text-lg font-medium">{account.bank_name}</p>
                            <span className="text-gray-400">•</span>
                            <p className="text-sm">{account.account_number}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleExpanded(account.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            data-testid={`toggle-transactions-${account.id}`}
                          >
                            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                          </button>
                          {editMode && (
                            <>
                              <button
                                onClick={() => handleEdit(account)}
                                className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                data-testid={`delete-mtf-${account.id}`}
                                onClick={() => handleDelete(account.id)}
                                className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Total Loan</p>
                          <p className="text-xl font-bold text-[#EA580C]">
                            ₹{account.total_loan.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Interest Rate</p>
                          <p className="text-xl font-bold">{account.interest_rate}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Monthly Interest</p>
                          <p className="text-xl font-bold">₹{monthlyInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Transactions</p>
                          <p className="text-xl font-bold">{accountTxns.length}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Total Value</p>
                          <p className="text-xl font-bold text-primary">₹{totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                        </div>
                      </div>

                      {/* Expanded Transactions View */}
                      {isExpanded && (
                        <div className="border-t pt-4 mt-4">
                          {/* Filter Controls */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <h4 className="font-semibold flex items-center gap-2">
                                <FileText size={18} />
                                Transactions ({filteredTxns.length} of {accountTxns.length})
                              </h4>
                              <Button
                                onClick={() => toggleFilters(account.id)}
                                className={`btn-outline text-sm flex items-center gap-1 ${hasActiveFilters ? 'border-primary text-primary' : ''}`}
                              >
                                <Filter size={14} />
                                Filters {hasActiveFilters && '•'}
                              </Button>
                              {hasActiveFilters && (
                                <button 
                                  onClick={() => clearFilters(account.id)}
                                  className="text-sm text-primary hover:underline"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              Click column headers to sort
                            </div>
                          </div>

                          {/* Filter Panel */}
                          {filtersVisible && (
                            <div className="p-4 bg-gray-50 rounded-lg mb-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <label className="block text-sm font-medium mb-1">Symbol</label>
                                <select
                                  className="input-field w-full text-sm"
                                  value={filters.symbol}
                                  onChange={(e) => handleFilterChange(account.id, 'symbol', e.target.value)}
                                >
                                  <option value="">All Symbols</option>
                                  {getUniqueSymbols(account.id).map(symbol => (
                                    <option key={symbol} value={symbol}>{symbol}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Type</label>
                                <select
                                  className="input-field w-full text-sm"
                                  value={filters.type}
                                  onChange={(e) => handleFilterChange(account.id, 'type', e.target.value)}
                                >
                                  <option value="">All Types</option>
                                  <option value="buy">Buy</option>
                                  <option value="buy_mtf">Buy (MTF)</option>
                                  <option value="sell">Sell</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                                  <Calendar size={12} /> From Date
                                </label>
                                <input
                                  type="date"
                                  className="input-field w-full text-sm"
                                  value={filters.dateFrom}
                                  onChange={(e) => handleFilterChange(account.id, 'dateFrom', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                                  <Calendar size={12} /> To Date
                                </label>
                                <input
                                  type="date"
                                  className="input-field w-full text-sm"
                                  value={filters.dateTo}
                                  onChange={(e) => handleFilterChange(account.id, 'dateTo', e.target.value)}
                                />
                              </div>
                            </div>
                          )}

                          {filteredTxns.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-gray-50">
                                    {renderSortHeader(account.id, 'date', 'Date')}
                                    {renderSortHeader(account.id, 'type', 'Type')}
                                    {renderSortHeader(account.id, 'symbol', 'Symbol')}
                                    {renderSortHeader(account.id, 'quantity', 'Quantity')}
                                    {renderSortHeader(account.id, 'price', 'Price')}
                                    {renderSortHeader(account.id, 'total', 'Total')}
                                    <th className="text-left py-3 px-3">Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredTxns.map((txn) => {
                                    const txnDate = new Date(txn.transaction_date).toLocaleDateString('en-IN');
                                    const total = (txn.quantity || 0) * (txn.price || 0);
                                    
                                    return (
                                      <tr key={txn.id} className="border-b hover:bg-gray-50">
                                        <td className="py-3 px-3 text-gray-600">{txnDate}</td>
                                        <td className="py-3 px-3">
                                          <span className={`badge text-xs ${
                                            txn.transaction_type === 'sell' ? 'badge-danger' : 'badge-success'
                                          }`}>
                                            {getTransactionTypeLabel(txn.transaction_type)}
                                          </span>
                                        </td>
                                        <td className="py-3 px-3 font-semibold">{txn.symbol}</td>
                                        <td className="py-3 px-3">{(txn.quantity || 0).toLocaleString()}</td>
                                        <td className="py-3 px-3">₹{(txn.price || 0).toFixed(2)}</td>
                                        <td className="py-3 px-3 font-semibold">₹{total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                        <td className="py-3 px-3 text-gray-500 text-xs max-w-[200px] truncate">
                                          {txn.notes || '-'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-gray-100 font-semibold">
                                    <td colSpan={3} className="py-3 px-3">Total</td>
                                    <td className="py-3 px-3">
                                      {filteredTxns.reduce((sum, t) => sum + (t.quantity || 0), 0).toLocaleString()}
                                    </td>
                                    <td className="py-3 px-3">-</td>
                                    <td className="py-3 px-3">
                                      ₹{filteredTxns.reduce((sum, t) => sum + ((t.quantity || 0) * (t.price || 0)), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                    </td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              {accountTxns.length === 0 
                                ? 'No transactions found for this account. Add transactions using the Transactions page.'
                                : 'No transactions match your filters.'}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex gap-3 mt-4">
                        <button
                          data-testid={`edit-mtf-loan-${account.id}`}
                          onClick={() => {
                            const newAmount = prompt('Enter new loan amount:', account.total_loan);
                            if (newAmount && !isNaN(newAmount)) {
                              updateLoan(account.id, parseFloat(newAmount));
                            }
                          }}
                          className="btn-outline flex items-center gap-2"
                        >
                          <Edit2 size={16} />
                          Update Loan
                        </button>
                        <button
                          onClick={() => toggleExpanded(account.id)}
                          className="btn-secondary flex items-center gap-2"
                          data-testid={`view-transactions-btn-${account.id}`}
                        >
                          <FileText size={16} />
                          {isExpanded ? 'Hide' : 'View'} Transactions
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MTFManagement;
